'use client';

import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { MetaDefault } from '../../../components/typography';
import { Select } from '../../../components/select';
import { SettingsRow } from '../../settings/SettingsRow';
import { Switch } from '../../../components/switch';
import { TechnologyIcon } from '../../technology-icon/TechnologyIcon';
import { Tooltip } from '../../../components/tooltip';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Tick01Icon,
  ArrowUpDownIcon,
  RefreshIcon,
  Unlink01Icon,
} from '@terra/ui-icons';
import { formatCloudProviderLabel } from '@terra/shared-contracts-integrations';
import type { CloudInventorySurfaceAttachmentDto } from '@terra/shared-contracts-external-network';
import { CatalogRowIconAction } from './CatalogRowIconAction';
import {
  AUTO_SCOPE_OPTIONS,
  STATUS_COLOR,
  resolveAutoScope,
  type AutoScopeCollection,
  type CloudAccountAutoScope,
} from './cloud-inventory-attach.utils';

type CloudAccountCatalogRowProps = {
  attachment: CloudInventorySurfaceAttachmentDto;
  isReadOnly: boolean;
  isBusy: boolean;
  isSyncing: boolean;
  isAttaching: boolean;
  isDetaching: boolean;
  lastSyncedText: string;
  neverSyncedLabel: string;
  scopeCollection: AutoScopeCollection;
  onAttach: (attachment: CloudInventorySurfaceAttachmentDto) => void;
  onDetach: (attachment: CloudInventorySurfaceAttachmentDto) => void;
  onSync: (attachment: CloudInventorySurfaceAttachmentDto) => void;
  onEnabledChange: (input: {
    accountId: string;
    autoScope: CloudAccountAutoScope;
    enabled: boolean;
  }) => void;
  onAutoScopeChange: (input: {
    accountId: string;
    autoScope: CloudAccountAutoScope;
  }) => void;
  onRequestInScope: (accountId: string) => void;
  labels: {
    enabled: string;
    scopePlaceholder: string;
    attach: string;
    detach: string;
    runSync: string;
    status: (
      status: NonNullable<CloudInventorySurfaceAttachmentDto['status']>,
    ) => string;
  };
};

export function CloudAccountCatalogRow({
  attachment,
  isReadOnly,
  isBusy,
  isSyncing,
  isAttaching,
  isDetaching,
  lastSyncedText,
  neverSyncedLabel,
  scopeCollection,
  onAttach,
  onDetach,
  onSync,
  onEnabledChange,
  onAutoScopeChange,
  onRequestInScope,
  labels,
}: CloudAccountCatalogRowProps) {
  const providerLabel = formatCloudProviderLabel({
    provider: attachment.provider,
  });
  const description =
    attachment.status === 'SYNC_ERROR' && attachment.lastError
      ? attachment.lastError
      : lastSyncedText;
  const autoScope = resolveAutoScope(attachment.autoScope);
  const accountId = attachment.accountId;
  const title =
    attachment.displayName && attachment.displayName !== attachment.accountRef
      ? attachment.displayName
      : (attachment.accountRef ?? providerLabel);
  const accountRefSubtitle =
    attachment.accountRef && attachment.accountRef !== title
      ? attachment.accountRef
      : null;

  return (
    <SettingsRow
      leadingIcon={
        <TechnologyIcon name={attachment.provider} size={16} showInitials />
      }
      label={title}
      description={
        <VStack alignItems="flex-start" gap={0.5}>
          {accountRefSubtitle ? (
            <MetaDefault color="fg.neutral.subtle">
              {accountRefSubtitle}
            </MetaDefault>
          ) : null}
          {accountId ? (
            <HStack gap={2} alignItems="center">
              {attachment.status && attachment.status !== 'ACTIVE' ? (
                <Badge
                  variant="outline"
                  size="sm"
                  colorPalette={STATUS_COLOR[attachment.status]}
                >
                  {labels.status(attachment.status)}
                </Badge>
              ) : null}
              <MetaDefault color="fg.neutral.subtle">{description}</MetaDefault>
            </HStack>
          ) : accountRefSubtitle ? null : (
            <MetaDefault color="fg.neutral.subtle">
              {neverSyncedLabel}
            </MetaDefault>
          )}
        </VStack>
      }
      control={
        <HStack gap={2} alignItems="center">
          {attachment.attached && accountId ? (
            <>
              <Box minW="9.5rem">
                <Select.Root
                  collection={scopeCollection}
                  value={[autoScope]}
                  disabled={isReadOnly || isBusy || !attachment.enabled}
                  onValueChange={({ value }) => {
                    const nextAutoScope = AUTO_SCOPE_OPTIONS.find(
                      (option) => option === value[0],
                    );

                    if (!nextAutoScope || nextAutoScope === autoScope) {
                      return;
                    }

                    if (nextAutoScope === 'IN_SCOPE') {
                      onRequestInScope(accountId);

                      return;
                    }

                    onAutoScopeChange({
                      accountId,
                      autoScope: nextAutoScope,
                    });
                  }}
                  positioning={{ sameWidth: true, strategy: 'fixed' }}
                  size="sm"
                  width="full"
                >
                  <Select.Control>
                    <Select.Trigger>
                      <Select.ValueText placeholder={labels.scopePlaceholder} />
                      <ArrowUpDownIcon size={14} />
                    </Select.Trigger>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {scopeCollection.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator>
                            <Tick01Icon size={14} />
                          </Select.ItemIndicator>
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </Box>
              <CatalogRowIconAction
                label={labels.runSync}
                disabled={isReadOnly || isBusy}
                loading={isSyncing}
                onClick={() => onSync(attachment)}
              >
                <RefreshIcon size={16} />
              </CatalogRowIconAction>
              <CatalogRowIconAction
                label={labels.detach}
                disabled={isReadOnly || isBusy}
                loading={isDetaching}
                onClick={() => onDetach(attachment)}
              >
                <Unlink01Icon size={16} />
              </CatalogRowIconAction>
              <Tooltip
                title={labels.enabled}
                positioning={{ strategy: 'fixed' }}
              >
                <Switch
                  size="sm"
                  checked={attachment.enabled ?? false}
                  disabled={isReadOnly || isBusy}
                  aria-label={labels.enabled}
                  onCheckedChange={({ checked }) => {
                    if (checked === (attachment.enabled ?? false)) {
                      return;
                    }

                    onEnabledChange({
                      accountId,
                      autoScope,
                      enabled: checked,
                    });
                  }}
                />
              </Tooltip>
            </>
          ) : (
            <>
              <CatalogRowIconAction
                label={labels.runSync}
                disabled={isReadOnly || isBusy}
                loading={isSyncing}
                onClick={() => onSync(attachment)}
              >
                <RefreshIcon size={16} />
              </CatalogRowIconAction>
              {accountId ? (
                <Button
                  size="xs"
                  disabled={isReadOnly || isBusy}
                  loading={isAttaching}
                  onClick={() => onAttach(attachment)}
                >
                  {labels.attach}
                </Button>
              ) : null}
            </>
          )}
        </HStack>
      }
    />
  );
}
